import React, { createContext, useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Sidebar from '../components/Sidebar';

interface MainLayoutProps {
    children: React.ReactNode;
}

const DrawerContext = createContext<{ open: () => void }>({ open: () => undefined });

/**
 * Lets a page's own header render the mobile menu button inline, instead of a
 * floating button that would have to out-z-index every modal on the page.
 */
export const useDrawer = () => useContext(DrawerContext);

const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
    // Drawer state is mobile-only; at md+ the sidebar is always visible and this
    // is never read.
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const location = useLocation();

    // Close the drawer on navigation so it never covers the page it opened.
    useEffect(() => {
        setIsDrawerOpen(false);
    }, [location.pathname]);

    return (
        <DrawerContext.Provider value={{ open: () => setIsDrawerOpen(true) }}>
            {/* app-shell carries the mobile-only 100dvh (see index.css) so the
                shell tracks the on-screen keyboard. It must not be an inline
                style: that would win at every width and change the desktop
                height, which clips the bottom-anchored notification panel. */}
            <div className="app-shell h-screen overflow-hidden flex bg-background">
                <Sidebar isOpen={isDrawerOpen} onClose={() => setIsDrawerOpen(false)} />

                {/* Backdrop — mobile only, under the drawer (z-40) and over content. */}
                {isDrawerOpen && (
                    <div
                        className="fixed inset-0 z-[55] bg-black/50 md:hidden"
                        onClick={() => setIsDrawerOpen(false)}
                        aria-hidden="true"
                    />
                )}

                {children}
            </div>
        </DrawerContext.Provider>
    );
};

export default MainLayout;
